import { router } from "@inertiajs/react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Filter,
  RotateCcw,
  Save,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HelpButton, HelpDialog } from "@/components/help-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHelpDialog } from "@/hooks/use-help-dialog";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { PriceComparison, PriceComparisonPrice } from "@/types";

interface Props {
  comparison: PriceComparison;
  purchaseRequestId: number;
  canSaveSelections?: boolean;
  // Optional extra info about the PR to enrich exports
  purchaseRequestInfo?: {
    id: number;
    request_number?: string;
    sector?: {
      id?: number | null;
      name?: string | null;
      code?: string | null;
    } | null;
  };
  // Frontend-only gate: allow exports only if approval flow is completed
  canExport?: boolean;
  onSaved?: () => void;
}

const formatCurrency = (amount: number, currency = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(
    amount,
  );

function SupplierFilterPopover({
  suppliers,
  visibleSupplierIds,
  onToggle,
  onShowAll,
}: {
  suppliers: { id: number; name: string }[];
  visibleSupplierIds: Set<number>;
  onToggle: (id: number) => void;
  onShowAll: () => void;
}) {
  const allVisible = visibleSupplierIds.size === suppliers.length;
  const hiddenCount = suppliers.length - visibleSupplierIds.size;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="size-4" />
          <span className="hidden sm:inline">Proveedores</span>
          {hiddenCount > 0 && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({hiddenCount} ocultos)
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Mostrar Proveedores</span>
            {!allVisible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowAll}
                className="h-auto p-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <RotateCcw className="size-3 mr-1" />
                Ver todos
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {suppliers.map((supplier) => {
              const isVisible = visibleSupplierIds.has(supplier.id);
              const canHide = visibleSupplierIds.size > 1;
              return (
                <Tooltip key={supplier.id}>
                  <TooltipTrigger asChild>
                    <div
                      key={supplier.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        id={`supplier-filter-${supplier.id}`}
                        checked={isVisible}
                        disabled={isVisible && !canHide}
                        onCheckedChange={() => onToggle(supplier.id)}
                      />
                      <Label
                        htmlFor={`supplier-filter-${supplier.id}`}
                        className="text-sm cursor-pointer flex-1 truncate"
                      >
                        {supplier.name}
                      </Label>
                      {isVisible && (
                        <Eye className="size-3 text-muted-foreground" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="start">
                    {supplier.name}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function PurchaseSelectionTable({
  comparison,
  purchaseRequestId,
  canSaveSelections = true,
  canExport = false,
  onSaved,
}: Props) {
  const routes = useOrganizationRoutes();
  const initial = useMemo(() => {
    const map = new Map<number, number>(); // request_item_id -> quote_item_id
    comparison.rows.forEach((row) => {
      row.prices.forEach((price) => {
        if (
          price?.is_selected &&
          price.request_item_id &&
          price.quote_item_id
        ) {
          map.set(price.request_item_id, price.quote_item_id);
        }
      });
    });
    return map;
  }, [comparison.rows]);

  const [selection, setSelection] = useState<Map<number, number>>(initial);

  const help = useHelpDialog("purchaseSelection");

  // Scroll navigation state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  const scrollByDirection = useCallback((direction: "left" | "right") => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const columnWidth = 120; // supplier column width
    el.scrollBy({
      left: direction === "left" ? -columnWidth * 2 : columnWidth * 2,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  // Supplier filter state - only track what user hid
  const [hiddenSupplierIds, setHiddenSupplierIds] = useState<Set<number>>(
    new Set(),
  );
  const visibleSupplierIds = new Set(
    comparison.headers.suppliers
      .map((s) => s.id)
      .filter((id) => !hiddenSupplierIds.has(id)),
  );

  const toggleSupplier = useCallback((supplierId: number) => {
    setHiddenSupplierIds((prev) => {
      const next = new Set(prev);
      if (next.has(supplierId)) {
        next.delete(supplierId);
      } else {
        next.add(supplierId);
      }
      return next;
    });
  }, []);

  const showAllSuppliers = useCallback(() => {
    setHiddenSupplierIds(new Set());
  }, []);

  const filteredSuppliers = useMemo(
    () =>
      comparison.headers.suppliers.filter((s) => visibleSupplierIds.has(s.id)),
    [comparison.headers.suppliers, visibleSupplierIds],
  );

  const handleCellClick = (
    requestItemId: number,
    price: PriceComparisonPrice | null | undefined,
  ) => {
    if (!price) return;
    if (price.missing_exchange_rate) return; // Deshabilitar USD sin TC
    if (!price.quote_item_id) return;
    const next = new Map(selection);
    if (selection.get(requestItemId) === price.quote_item_id) {
      next.delete(requestItemId);
    } else {
      next.set(requestItemId, price.quote_item_id);
    }
    setSelection(next);
  };

  const handleSave = () => {
    const selections: { request_item_id: number; quote_item_id: number }[] = [];
    selection.forEach((quote_item_id, request_item_id) =>
      selections.push({ request_item_id, quote_item_id }),
    );

    const removedRequestItemIds: number[] = [];
    initial.forEach((_quoteItemId, requestItemId) => {
      if (!selection.has(requestItemId)) {
        removedRequestItemIds.push(requestItemId);
      }
    });

    const payload: {
      selections: { request_item_id: number; quote_item_id: number }[];
      remove_request_item_ids?: number[];
    } = { selections };

    if (removedRequestItemIds.length > 0) {
      payload.remove_request_item_ids = removedRequestItemIds;
    }

    router.post(
      routes.purchaseRequests.purchaseSelections.bulkUpsert(purchaseRequestId),
      payload,
      {
        preserveScroll: true,
        onSuccess: () => onSaved?.(),
      },
    );
  };

  const selectedSupplierIds = useMemo(() => {
    const ids = new Set<number>();
    comparison.rows.forEach((row) => {
      const reqId = row.request_item.id;
      const quoteItemId = selection.get(reqId);
      if (!quoteItemId) return;
      // Find the supplier for that quoteItem in this row
      row.prices.forEach((price) => {
        if (price && price.quote_item_id === quoteItemId && price.supplier_id) {
          ids.add(price.supplier_id);
        }
      });
    });
    return Array.from(ids);
  }, [selection, comparison.rows]);

  // Build backend export URL including selected supplier filters
  const jsonExportHref = (() => {
    const base = routes.purchaseRequests.show(purchaseRequestId);
    return `${base}/export_purchase_selection_json`;
  })();

  if (!comparison?.rows.length) return null;

  // Progress tracking
  const totalMaterials = comparison.rows.length;
  const selectedCount = selection.size;
  const progressPercent =
    totalMaterials > 0 ? (selectedCount / totalMaterials) * 100 : 0;
  const isComplete = selectedCount === totalMaterials;

  return (
    <div className="w-full">
      {/* Progress bar */}
      <div className="mb-4 p-4 bg-muted/50 rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">
            {selectedCount} de {totalMaterials} materiales seleccionados
          </span>
          {isComplete ? (
            <span className="text-sm text-green-600 font-medium flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              ¡Completo!
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              Faltan {totalMaterials - selectedCount}
            </span>
          )}
        </div>
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 rounded-full ${
              isComplete ? "bg-green-500" : "bg-blue-500"
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          Haz clic en la celda del proveedor que quieras para cada material.
        </div>
        <ButtonGroup>
          {comparison.headers.suppliers.length > 1 && (
            <SupplierFilterPopover
              suppliers={comparison.headers.suppliers}
              visibleSupplierIds={visibleSupplierIds}
              onToggle={toggleSupplier}
              onShowAll={showAllSuppliers}
            />
          )}
          <HelpButton onClick={help.open} label="Ayuda" variant="outline" />
        </ButtonGroup>
      </div>
      <div className="relative">
        {/* Left navigation button */}
        {canScrollLeft && (
          <button
            onClick={() => scrollByDirection("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white shadow-lg border-0 rounded-full p-2.5 transition-all hover:scale-110"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="max-h-[85vh] overflow-y-scroll overflow-x-scroll rounded-lg border [&_[data-slot=table-container]]:!overflow-visible"
        >
          <Table>
            <TableHeader className="sticky top-0 z-20 bg-muted after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[2px] after:bg-slate-900 dark:after:bg-slate-100">
              <TableRow>
                <TableHead className="min-w-[200px] max-w-[280px] sticky left-0 top-0 z-30 bg-muted font-semibold after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100">
                  Artículo
                </TableHead>
                <TableHead className="text-center w-20 px-2 font-semibold border-r">
                  Cant.
                </TableHead>
                <TableHead className="text-center w-16 px-2 font-semibold border-r">
                  U.M.
                </TableHead>
                {filteredSuppliers.map((s) => (
                  <TableHead
                    key={s.quote_id}
                    className="text-center min-w-[120px] max-w-[150px] px-3 font-semibold border-r last:border-r-0"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="truncate cursor-default max-w-[130px] mx-auto">
                          {s.name}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">{s.name}</TooltipContent>
                    </Tooltip>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.rows.map((row, rowIndex) => {
                const selectedQuoteItemId = selection.get(row.request_item.id);
                const hasSelection = selectedQuoteItemId !== undefined;
                // Solid backgrounds for alternating rows
                const isOddRow = rowIndex % 2 === 1;
                const rowBg = isOddRow
                  ? "bg-slate-100 dark:bg-slate-800"
                  : "bg-white dark:bg-slate-900";
                const stickyBg = isOddRow
                  ? "bg-slate-100 dark:bg-slate-800"
                  : "bg-white dark:bg-slate-900";
                // Row highlight: subtle green left border if has selection
                const selectionIndicator = hasSelection
                  ? "border-l-4 border-l-green-500"
                  : "border-l-4 border-l-transparent";
                return (
                  <TableRow
                    key={row.request_item.id}
                    className={`${rowBg} ${selectionIndicator} group hover:!bg-amber-50 dark:hover:!bg-amber-900/30 hover:shadow-[inset_0_0_0_1px_rgba(251,191,36,0.4)] transition-all duration-150`}
                  >
                    <TableCell
                      className={`max-w-[280px] sticky left-0 ${stickyBg} group-hover:!bg-amber-50 dark:group-hover:!bg-amber-900/30 z-10 font-medium transition-all duration-150 after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-[2px] after:bg-slate-900 dark:after:bg-slate-100`}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="max-w-[220px] truncate cursor-default">
                            {row.request_item.description}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="max-w-[350px] whitespace-normal"
                        >
                          <div className="font-medium">
                            {row.request_item.description}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Cantidad: {row.request_item.quantity}{" "}
                            {row.request_item.unit}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-center tabular-nums border-r">
                      {row.request_item.quantity}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground text-sm border-r">
                      {row.request_item.unit}
                    </TableCell>
                    {filteredSuppliers.map((supplier) => {
                      // Find the original index to get the correct price
                      const originalIndex =
                        comparison.headers.suppliers.findIndex(
                          (s) => s.id === supplier.id,
                        );
                      const price = row.prices[originalIndex];
                      const disabled = [
                        price == null,
                        price?.missing_exchange_rate,
                        price?.unit_price_ars === null,
                      ].some(Boolean);
                      const isSelected =
                        price && price.quote_item_id === selectedQuoteItemId;
                      const isBestPrice = !!price?.is_best_price;
                      return (
                        <Tooltip
                          key={`c-${row.request_item.id}-${supplier.id}`}
                        >
                          <TooltipTrigger asChild>
                            <TableCell
                              onClick={() =>
                                !disabled &&
                                handleCellClick(row.request_item.id, price)
                              }
                              className={`text-center px-3 py-2 border-r last:border-r-0 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${isSelected ? "bg-green-100 dark:bg-green-900/30 ring-2 ring-inset ring-green-500" : !disabled ? "hover:bg-blue-50 dark:hover:bg-blue-900/20" : ""}`}
                            >
                              {price ? (
                                <div className="space-y-1">
                                  <div className="font-medium">
                                    {price.unit_price_ars !== null ? (
                                      formatCurrency(
                                        price.unit_price_ars,
                                        "ARS",
                                      )
                                    ) : (
                                      <span className="text-muted-foreground">
                                        No disponible
                                      </span>
                                    )}
                                  </div>
                                  {!price.missing_exchange_rate &&
                                    price.unit_price_ars !== null &&
                                    isBestPrice && (
                                      <Badge
                                        variant="default"
                                        className="mt-1 text-xs bg-green-600"
                                      >
                                        Mejor precio
                                      </Badge>
                                    )}
                                  {price.missing_exchange_rate && (
                                    <div className="flex items-center justify-center gap-1 text-amber-700 text-xs">
                                      <AlertTriangle className="h-3 w-3" /> Sin
                                      tipo de cambio
                                    </div>
                                  )}
                                  {isSelected && (
                                    <div className="flex items-center justify-center gap-1 text-green-700 text-xs">
                                      <CheckCircle2 className="h-3 w-3" />{" "}
                                      Seleccionado
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-muted-foreground">-</div>
                              )}
                            </TableCell>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[320px]">
                            <div className="space-y-2">
                              <div>
                                <div className="font-medium">
                                  {row.request_item.description}
                                </div>
                                <div className="text-xs text-primary-foreground/70">
                                  Cantidad solicitada:{" "}
                                  {row.request_item.quantity}{" "}
                                  {row.request_item.unit}
                                </div>
                              </div>
                              {price && price.unit_price_ars !== null && (
                                <div className="pt-1 border-t border-primary-foreground/30">
                                  {isBestPrice ? (
                                    <div className="text-xs text-green-300 font-medium">
                                      ✓ Este es el mejor precio para este
                                      material
                                    </div>
                                  ) : price.percentage_diff_from_best != null &&
                                    Number(price.percentage_diff_from_best) >
                                      0 ? (
                                    <div className="text-xs text-orange-300">
                                      <span className="font-semibold">
                                        +
                                        {Number(
                                          price.percentage_diff_from_best,
                                        ).toFixed(1)}
                                        %
                                      </span>{" "}
                                      más caro que el mejor precio
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Right navigation button */}
        {canScrollRight && (
          <button
            onClick={() => scrollByDirection("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white shadow-lg border-0 rounded-full p-2.5 transition-all hover:scale-110"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="pt-4">
        <div className="flex items-center justify-end gap-2">
          {selectedSupplierIds.length > 0 &&
            (canExport ? (
              <>
                <Button size="sm" asChild>
                  <a
                    href={routes.purchaseRequests.exportPurchaseOrders(
                      purchaseRequestId,
                      { supplier_ids: selectedSupplierIds },
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="h-4 w-4 mr-1" /> Exportar orden(es)
                  </a>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={jsonExportHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileText className="h-4 w-4 mr-1" /> Exportar JSON
                  </a>
                </Button>
              </>
            ) : (
              <div className="text-sm text-muted-foreground mr-auto">
                Para exportar las órdenes o el JSON de compra, primero debes
                aprobar la solicitud en el flujo de aprobaciones.
              </div>
            ))}
          {canSaveSelections && (
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant={"outline"}
                    onClick={handleSave}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="h-4 w-4 mr-1" /> Guardar selección
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px]">
                  Podrás seguir editando la selección hasta que la solicitud
                  entre en aprobación.
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <HelpDialog
        content={help.content}
        open={help.isOpen}
        onOpenChange={help.setIsOpen}
      />
    </div>
  );
}
